import React, { useEffect, useState } from "react";

import { yupResolver } from "@hookform/resolvers/yup";
import { signUp } from "aws-amplify/auth";
import i18n from "i18next";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { Helmet } from "react-helmet";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import * as Yup from "yup";

import { useValidateRecaptcha } from "@api";

import { usePartnerInfo, withController } from "@utils";

import { Button, Divider, Notification, Text } from "@ui/elements";
import { Checkbox, Input } from "@ui/forms";
import { Cell, Grid, Stack } from "@ui/layout";

import { useAuth } from "@components";

import { SocialLogin } from "../../components/SocialLogin/SocialLogin";

type RegisterFields = {
  email: string;
  password: string;
  confirmPassword: string;
  given_name: string;
  last_name: string;
  termsAndConditions?: boolean | undefined;
  marketingUpdates?: boolean | undefined;
};

const ControlledTextInput = withController<RegisterFields>()(Input);
const ControlledCheckbox = withController<RegisterFields>()(Checkbox);

export const SignUp = () => {
  const { t } = useTranslation();
  const formSchema = Yup.object().shape({
    given_name: Yup.string().required(t("messages.first-name-required")),
    last_name: Yup.string().required(t("messages.last-name-required")),
    termsAndConditions: Yup.boolean().oneOf(
      [true],
      t("messages.terms-and-conditions-required"),
    ),
    marketingUpdates: Yup.boolean(),
    email: Yup.string().required(t("messages.email-required")),
    password: Yup.string().required(t("messages.password-required")),
    confirmPassword: Yup.string()
      .required(t("messages.confirm-password-required"))
      .oneOf([Yup.ref("password")], t("messages.password-mismatch")),
  });
  const [searchParams] = useSearchParams();

  const { control, handleSubmit } = useForm<RegisterFields>({
    defaultValues: {
      given_name: "",
      last_name: "",
      password: "",
      confirmPassword: "",
      email: searchParams.get("email") || "",
      termsAndConditions: false,
    },
    resolver: yupResolver(formSchema),
  });

  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { mutateAsync: validateRecaptcha, isPending: recaptchaLoading } =
    useValidateRecaptcha();
  const navigate = useNavigate();
  const authContext = useAuth();
  const partnerInfo = usePartnerInfo();

  useEffect(() => {
    if (authContext.isAuthenticated === true) {
      navigate("/");
    }
  }, [authContext.isAuthenticated]);

  const onSubmit = async (values: RegisterFields) => {
    setLoading(true);
    setError("");

    // Registration
    try {
      if (!executeRecaptcha) {
        console.log("Execute recaptcha not yet available");
        return;
      }

      const token = await executeRecaptcha("signup");
      const recaptchaResponse = await validateRecaptcha({ data: { token } });

      if (!recaptchaResponse.success) {
        setError(t("messages.unexpected-error"));
        return;
      }

      await signUp({
        username: values.email,
        password: values.password,
        options: {
          userAttributes: {
            family_name: values.last_name,
            given_name: values.given_name,
            name: `${values.given_name} ${values.last_name}`,
            "custom:platformUpdates": "true",
            "custom:feedbackSurvey": "true",
            "custom:marketingEmails": String(!!values.marketingUpdates),
            "custom:newsLetters": String(!!values.marketingUpdates),
          },
          clientMetadata: {
            locale: i18n.language,
            partnerId: process.env.REACT_APP_PARTNER_ID!,
          },
        },
      });

      const params = new URLSearchParams(location.search);

      params.set("email", values.email);

      navigate({
        pathname: "/confirm-email",
        search: `?${params.toString()}`,
      });
    } catch (error) {
      setLoading(false);

      if (error instanceof Error) {
        return setError(
          t(error.message.replace("PreSignUp failed with error ", "")),
        );
      }

      setError(String(error));
    } finally {
      setLoading(false);
    }
  };

  const errorMessage = searchParams.get("error_description") || error;
  const loadingState = recaptchaLoading || loading;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Helmet>
        <title>
          {t("labels.login")} - {partnerInfo.name}
        </title>
      </Helmet>
      <Stack direction="column" gap="xxl">
        <Stack direction="column" gap="xl">
          <Stack direction="column" gap="xl">
            <Stack direction="column" gap="xl">
              <Stack direction="column">
                <ControlledTextInput
                  label={t("labels.email")}
                  name="email"
                  type="email"
                  control={control}
                  autoComplete="username"
                />
                <Grid>
                  <Cell>
                    <ControlledTextInput
                      label={t("labels.first-name")}
                      control={control}
                      name="given_name"
                      autoComplete="given-name"
                      type="text"
                    />
                  </Cell>
                  <Cell>
                    <ControlledTextInput
                      label={t("labels.last-name")}
                      control={control}
                      name="last_name"
                      type="text"
                      autoComplete="family-name"
                    />
                  </Cell>
                </Grid>
              </Stack>
              <Divider />
              <Stack direction="column">
                <ControlledTextInput
                  label={t("labels.password")}
                  control={control}
                  name="password"
                  type="password"
                  autoComplete="new-password"
                />
                <ControlledTextInput
                  label={t("labels.confirm-password")}
                  control={control}
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                />
                <ControlledCheckbox
                  control={control}
                  name="marketingUpdates"
                  isCentered={false}
                >
                  <Text size="small">{t("messages.marketing-checkbox")}</Text>
                </ControlledCheckbox>
                <ControlledCheckbox
                  control={control}
                  name="termsAndConditions"
                  isCentered={false}
                >
                  <Text
                    size="small"
                    dangerouslySetInnerHTML={{
                      __html: t("messages.terms-and-conditions"),
                    }}
                  ></Text>
                </ControlledCheckbox>
              </Stack>
              <Stack direction="column">
                {errorMessage && (
                  <Notification color="danger">{errorMessage}</Notification>
                )}
                <Button color="primary" type="submit" isLoading={loadingState}>
                  {t("create-account")}
                </Button>
                <Link to="/login">
                  <Button color="text" isFullwidth>
                    {t("labels.back-to-login")}
                  </Button>
                </Link>
              </Stack>
            </Stack>

            <Divider text={t("or")} level={3} />

            <SocialLogin />
          </Stack>
        </Stack>
      </Stack>
    </form>
  );
};
